import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ForceChangePassword from './pages/ForceChangePassword';
import DashboardStatePanel from './pages/subscriber/DashboardStatePanel';
import SubscriberDecode from './pages/subscriber/Decode';
import SubscriberProtocol from './pages/subscriber/Protocol';
import SubscriberTracking from './pages/subscriber/Tracking';
import SubscriberReports from './pages/subscriber/Reports';
import SubscriberLabs from './pages/subscriber/MyLabs';
import MyIntake from './pages/subscriber/MyIntake';
import PerceptionShift from './pages/subscriber/PerceptionShift';
import SubscriberVideos from './pages/subscriber/Videos';
import SubscriberSupport from './pages/subscriber/Support';
import SubscriberWearables from './pages/subscriber/Wearables';
import DailyFlowExperience from './pages/subscriber/DailyFlowExperience';
import Onboarding from './pages/subscriber/Onboarding';
import MindFlo from './pages/subscriber/MindFlo';
import { useEngagementTracker } from './hooks/useEngagementTracker';
import AdminDashboard from './pages/admin/Dashboard';
import AdminCRM from './pages/admin/CRM';
import AdminUsers from './pages/admin/Users';
import AdminDocuments from './pages/admin/Documents';
import AdminCareOps from './pages/admin/CareOps';
import AdminTrackingOS from './pages/admin/TrackingOS';
import AdminFormulationRawBases from './pages/admin/FormulationRawBases';
import AdminFormulationDashboard from './pages/admin/FormulationDashboard';
import AdminNeuroFlowOverview from './pages/admin/NeuroFlowOverview';
import AdminWearableIntegrations from './pages/admin/WearableIntegrations';
import GrowthCommandCenter from './pages/admin/GrowthCommandCenter';
import PerceptionTrackerPage from './pages/counselor/PerceptionTracker';
import CounselorPipelinePage from './pages/counselor/Pipeline';
import CentreEventsPage from './pages/centre/CentreEvents';
import CentreGrowthDashboardPage from './pages/centre/CentreGrowthDashboard';
import DailyExecutionSheetPage from './pages/me/DailyExecutionSheet';
import FrictionMatrixPage from './pages/intelligence/FrictionMatrix';
import TerritoryMaturityPage from './pages/intelligence/TerritoryMaturity';
import EventTypeEfficiencyPage from './pages/intelligence/EventTypeEfficiency';
import EcosystemResistancePage from './pages/intelligence/EcosystemResistance';
import PerceptionLeakagePage from './pages/intelligence/PerceptionLeakage';
import CounselorCapabilityPage from './pages/intelligence/CounselorCapability';
import ReferralGraphPage from './pages/intelligence/ReferralGraph';
import RssIntelligencePage from './pages/intelligence/RssIntelligence';
import MovementDigestPage from './pages/intelligence/MovementDigest';
import TerritoryHeatmapsPage from './pages/intelligence/TerritoryHeatmaps';
import TerritoryMapPage from './pages/intelligence/TerritoryMap';
import GrowthImportPage from './pages/admin/GrowthImport';
import TerritoryEvolutionPage from './pages/intelligence/TerritoryEvolution';
import StewardshipLetterPage from './pages/centre/StewardshipLetter';
import MySustainabilityPage from './pages/sustainability/MySustainability';
import CounselorSustainabilityPage from './pages/sustainability/CounselorSustainability';
import WeeklyWarRoomPage from './pages/centre/WeeklyWarRoom';
import AdminNeuroFlowSubscriber from './pages/admin/NeuroFlowSubscriber';
import AdminNeuroFlowDoctorExport from './pages/admin/NeuroFlowDoctorExport';
import AdminFormulationProduction from './pages/admin/FormulationProduction';
import AdminFormulationInventory from './pages/admin/FormulationInventory';
import AdminFormulationVerification from './pages/admin/FormulationVerification';
import AdminFormulationSnapshot from './pages/admin/FormulationSnapshot';
import AdminFormulationFeedback from './pages/admin/FormulationFeedback';
import AdminFormulationTransitionPlan from './pages/admin/FormulationTransitionPlan';
import SubscriberTransitionPlan from './pages/subscriber/TransitionPlan';
import SubscriberPhasePreparation from './pages/subscriber/PhasePreparation';
import AdminClinical from './pages/admin/Clinical';
import AdminMindFloReview from './pages/admin/MindFloReview';
import EngagementDashboard from './pages/admin/EngagementDashboard';
import SupportDashboard from './pages/admin/SupportDashboard';
import AutonomyMonitoring from './pages/admin/AutonomyMonitoring';
import OSTodayPage from './pages/subscriber/OSTodayPage';
import { FacilitatorDashboard, TerraHeadDashboard, TerraInvestorDashboard } from './pages/terra/TerraDashboards';
import ActivateAccount from './pages/ActivateAccount';
import AdminContent from './pages/admin/Content';
import AdminAIWorkbench from './pages/admin/AIWorkbench';
import AdminAnalytics from './pages/admin/Analytics';
import AdminAuditLog from './pages/admin/AuditLog';
import AdminLabIntelligence from './pages/admin/LabIntelligenceV2';
import AdminDecodeWizard from './pages/admin/DecodeWorkspace';
import AdminBiologicalContextMemory from './pages/admin/BiologicalContextMemory';
import AdminGovernanceDrift from './pages/admin/GovernanceDrift';
import AdminSupportVisibility from './pages/admin/SupportVisibility';
import AdminProtocolEvolutions from './pages/admin/ProtocolEvolutions';
import AdminCounselorFollowUp from './pages/admin/CounselorFollowUp';
import AdminProtocolWizard from './pages/admin/ProtocolWizard';
import ProtocolFlowPage from './pages/admin/ProtocolFlowPage';
import ExecutableProtocolReview from './pages/admin/ExecutableProtocolReview';
import AssignedSubscribers from './pages/admin/AssignedSubscribers';
import CareTeamDashboardView from './pages/admin/CareTeamDashboardView';
import ImpersonateView from './pages/admin/ImpersonateView';
import AdminWearables from './pages/admin/Wearables';
import CareTeamInsights from './pages/admin/CareTeamInsights';
import ReportManagement from './pages/admin/ReportsHub';
import ContentPublishing from './pages/admin/ContentPublishing';
import ScheduleEditor from './pages/admin/ScheduleEditor';
import LifeFloDefinitions from './pages/admin/LifeFloDefinitions';
import MedicationDefinitions from './pages/admin/MedicationDefinitions';
import TimelineReview from './pages/admin/TimelineReview';
import ExecutionIntelligence from './pages/admin/ExecutionIntelligence';
import AdminBiologicalContext from './pages/admin/BiologicalContext';
import BioContextV2 from './pages/admin/BioContextV2';
import CorrelationInsights from './pages/admin/CorrelationInsights';
import PatternNarrative from './pages/admin/PatternNarrative';
import ReportGenerator from './pages/admin/ReportsHub';
import DataManagement from './pages/admin/DataManagement';
import DemoSimulation from './pages/admin/DemoSimulation';
import DemoControlCenter from './pages/admin/DemoControlCenter';
import { AdminCommandCenter, CounselorFlow, CoachExecutionFlow, CareFlowRoleSwitch } from './pages/admin/CareFlowPages';
import OrgStructure from './pages/admin/OrgStructure';
import JourneyManagement from './pages/admin/JourneyManagement';
import DeploymentDashboard from './pages/admin/DeploymentDashboard';
import OpsCockpit from './pages/admin/OpsCockpit';
import TwinFloEvolutionPage from './pages/admin/TwinFloEvolutionPage';
import TwinFloDashboard from './pages/subscriber/TwinFloDashboard';
import SimulationSandbox from './pages/subscriber/SimulationSandbox';
import MyReports from './pages/subscriber/MyReports';
import SubscriberKnowledge from './pages/subscriber/Knowledge';
import RecipeExperience from './pages/subscriber/RecipeExperience';

// API base - always use relative path so it works on any deployed domain
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = '/api';

// Token storage (in-memory + localStorage for persistence)
const TokenStore = {
  getAccessToken: () => localStorage.getItem('rf_access_token'),
  getRefreshToken: () => localStorage.getItem('rf_refresh_token'),
  setTokens: (access, refresh) => {
    if (access) localStorage.setItem('rf_access_token', access);
    if (refresh) localStorage.setItem('rf_refresh_token', refresh);
  },
  clear: () => {
    localStorage.removeItem('rf_access_token');
    localStorage.removeItem('rf_refresh_token');
  }
};

// Configure axios
axios.defaults.withCredentials = true;

// Axios interceptor: attach Authorization header
axios.interceptors.request.use((config) => {
  const token = TokenStore.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Axios interceptor: handle 401 and auto-refresh
let isRefreshing = false;
let failedQueue = [];

// All auth-related URLs should NEVER trigger the refresh flow
const AUTH_URLS = ['/auth/login', '/auth/refresh', '/auth/me', '/auth/register', '/auth/logout', '/auth/google', '/auth/forgot-password', '/auth/reset-password'];
const isAuthUrl = (url) => AUTH_URLS.some(path => url?.includes(path));

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh for non-auth API calls that haven't already been retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthUrl(originalRequest.url)
    ) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest._retry = true; // Prevent re-entry on retry failure
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = TokenStore.getRefreshToken();
      if (!refreshToken) {
        isRefreshing = false;
        processQueue(new Error('No refresh token'), null);
        TokenStore.clear();
        return Promise.reject(error);
      }

      try {
        // Use fetch() for the refresh call to avoid interceptor recursion
        const refreshResponse = await fetch(`${API}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!refreshResponse.ok) {
          throw new Error(`Refresh failed: ${refreshResponse.status}`);
        }

        const refreshData = await refreshResponse.json();
        const newAccessToken = refreshData.access_token;
        TokenStore.setTokens(newAccessToken, null);
        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        TokenStore.clear();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Format API error detail
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Use fetch() directly — NOT axios — to avoid triggering the 401 interceptor/refresh loop.
    // If the token is expired, we simply set user=null and let the user re-login.
    const token = TokenStore.getAccessToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Token expired or invalid — try refresh silently
        const refreshToken = TokenStore.getRefreshToken();
        if (refreshToken) {
          const refreshResp = await fetch(`${API}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
          if (refreshResp.ok) {
            const refreshData = await refreshResp.json();
            TokenStore.setTokens(refreshData.access_token, null);
            // Retry /me with new token
            const retryResp = await fetch(`${API}/auth/me`, {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${refreshData.access_token}`, 'Accept': 'application/json' },
              credentials: 'include',
            });
            if (retryResp.ok) {
              setUser(await retryResp.json());
              return;
            }
          }
        }
        // Refresh failed or no refresh token — clear everything
        TokenStore.clear();
        setUser(null);
        return;
      }

      setUser(await response.json());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, refresh_token, requires_password_change, ...userData } = response.data;
      TokenStore.setTokens(access_token, refresh_token);
      setUser(userData);
      if (requires_password_change) {
        return { success: true, user: userData, requires_password_change: true };
      }
      return { success: true, user: userData };
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (!error.response) {
        return { success: false, error: "Cannot reach the server. Please check your connection." };
      }
      if (status === 401 || status === 403) {
        return { success: false, error: formatApiErrorDetail(detail) || "Invalid email or password." };
      }
      if (status === 429) {
        return { success: false, error: formatApiErrorDetail(detail) || "Too many login attempts. Please wait and try again." };
      }
      return { success: false, error: formatApiErrorDetail(detail) || `Login failed (${status}). Please try again.` };
    }
  };

  const register = async (email, password, name, role = 'subscriber') => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
      const { access_token, refresh_token, ...userData } = response.data;
      TokenStore.setTokens(access_token, refresh_token);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    TokenStore.clear();
    setUser(null);
  };

  const googleLogin = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google/session?session_id=${sessionId}`);
      const { access_token, refresh_token, ...userData } = response.data;
      TokenStore.setTokens(access_token, refresh_token);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    googleLogin,
    checkAuth,
    isAdmin: user?.role === 'admin',
    isCareTeam: ['admin', 'counselor', 'health_coach', 'doctor'].includes(user?.role),
    isSubscriber: user?.role === 'subscriber',
    isCaregiver: user?.role === 'caregiver',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Theme Provider
function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('regenflo-theme');
    return stored || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('regenflo-theme', theme);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === 'admin' || ['counselor', 'health_coach', 'doctor'].includes(user.role)) {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'flow_steward') {
      return <Navigate to="/terra/facilitator" replace />;
    }
    if (user.role === 'terra_head') {
      return <Navigate to="/terra/head" replace />;
    }
    if (user.role === 'terra_investor') {
      return <Navigate to="/terra/investor" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// App Router
function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  
  // Engagement tracking (invisible, subscriber-only)
  useEngagementTracker(user);
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/activate" element={<ActivateAccount />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/change-password-required" element={<ForceChangePassword />} />
      
      {/* Subscriber Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <DashboardStatePanel />
        </ProtectedRoute>
      } />
      <Route path="/decode" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberDecode />
        </ProtectedRoute>
      } />
      <Route path="/protocol" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberProtocol />
        </ProtectedRoute>
      } />
      <Route path="/tracking" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberTracking />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberReports />
        </ProtectedRoute>
      } />
      {/* Redirect old /reports to canonical /my-reports is not needed — both render same component */}
      <Route path="/videos" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberVideos />
        </ProtectedRoute>
      } />
      <Route path="/insights" element={<Navigate to="/knowledge" replace />} />
      <Route path="/support" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberSupport />
        </ProtectedRoute>
      } />
      <Route path="/wearables" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver', 'admin', 'counselor', 'health_coach', 'doctor']}>
          <SubscriberWearables />
        </ProtectedRoute>
      } />
      <Route path="/daily" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver', 'admin', 'counselor', 'health_coach', 'doctor']}>
          <DailyFlowExperience />
        </ProtectedRoute>
      } />
      <Route path="/mindflo" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver', 'admin', 'counselor', 'health_coach', 'doctor']}>
          <MindFlo />
        </ProtectedRoute>
      } />
      <Route path="/twinflo" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <TwinFloDashboard />
        </ProtectedRoute>
      } />
      <Route path="/twinflo/simulate" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SimulationSandbox />
        </ProtectedRoute>
      } />
      <Route path="/setup" element={
        <ProtectedRoute allowedRoles={['subscriber']}>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/my-reports" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberReports />
        </ProtectedRoute>
      } />
      <Route path="/my-labs" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberLabs />
        </ProtectedRoute>
      } />
      <Route path="/my-intake" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <MyIntake />
        </ProtectedRoute>
      } />
      <Route path="/perception-shift" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <PerceptionShift />
        </ProtectedRoute>
      } />
      <Route path="/today-os" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <OSTodayPage />
        </ProtectedRoute>
      } />
      
      {/* Terra Routes */}
      <Route path="/terra/facilitator" element={
        <ProtectedRoute allowedRoles={['flow_steward', 'admin', 'counselor']}>
          <FacilitatorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/terra/head" element={
        <ProtectedRoute allowedRoles={['terra_head', 'admin']}>
          <TerraHeadDashboard />
        </ProtectedRoute>
      } />
      <Route path="/terra/investor" element={
        <ProtectedRoute allowedRoles={['terra_investor', 'admin']}>
          <TerraInvestorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/knowledge" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <SubscriberKnowledge />
        </ProtectedRoute>
      } />
      <Route path="/eat" element={
        <ProtectedRoute allowedRoles={['subscriber', 'caregiver']}>
          <RecipeExperience />
        </ProtectedRoute>
      } />

      {/* Admin / Care Team Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/crm" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor']}>
          <AdminCRM />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach']}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/documents" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach']}>
          <AdminDocuments />
        </ProtectedRoute>
      } />
      <Route path="/admin/care-ops" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach']}>
          <AdminCareOps />
        </ProtectedRoute>
      } />
      <Route path="/admin/tracking-os" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminTrackingOS />
        </ProtectedRoute>
      } />
      <Route path="/admin/formulation/raw-bases" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminFormulationRawBases />
        </ProtectedRoute>
      } />
      <Route path="/admin/formulation/dashboard" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminFormulationDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/neuroflow" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminNeuroFlowOverview />
        </ProtectedRoute>
      } />
      <Route path="/admin/neuroflow/:subscriberId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminNeuroFlowSubscriber />
        </ProtectedRoute>
      } />
      <Route path="/admin/neuroflow/:subscriberId/doctor-export" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'doctor']}>
          <AdminNeuroFlowDoctorExport />
        </ProtectedRoute>
      } />
      <Route path="/admin/wearable-integrations" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminWearableIntegrations />
        </ProtectedRoute>
      } />
      <Route path="/admin/growth-command-center" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <GrowthCommandCenter />
        </ProtectedRoute>
      } />
      <Route path="/counselor/perception-tracker" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'terra_head', 'representative']}>
          <PerceptionTrackerPage />
        </ProtectedRoute>
      } />
      <Route path="/counselor/pipeline" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'terra_head', 'representative']}>
          <CounselorPipelinePage />
        </ProtectedRoute>
      } />
      <Route path="/centre/events" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head', 'counselor', 'health_coach', 'representative']}>
          <CentreEventsPage />
        </ProtectedRoute>
      } />
      <Route path="/centre/growth-dashboard" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <CentreGrowthDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/me/execution-sheet" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'terra_head', 'representative']}>
          <DailyExecutionSheetPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/friction-matrix" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head', 'counselor', 'health_coach']}>
          <FrictionMatrixPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/territory-maturity" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <TerritoryMaturityPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/event-type-efficiency" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head', 'counselor', 'health_coach']}>
          <EventTypeEfficiencyPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/ecosystem-resistance" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head', 'counselor', 'health_coach']}>
          <EcosystemResistancePage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/perception-leakage" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head', 'counselor', 'health_coach']}>
          <PerceptionLeakagePage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/counselor-capability" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head', 'counselor', 'health_coach']}>
          <CounselorCapabilityPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/referral-graph" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head', 'counselor', 'health_coach']}>
          <ReferralGraphPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/rss-dashboard" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <RssIntelligencePage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/movement-digest" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <MovementDigestPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/territory-heatmaps" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <TerritoryHeatmapsPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/territory-map" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <TerritoryMapPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/growth-import" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <GrowthImportPage />
        </ProtectedRoute>
      } />
      <Route path="/intelligence/territory-evolution" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <TerritoryEvolutionPage />
        </ProtectedRoute>
      } />
      <Route path="/centre/stewardship-letter" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <StewardshipLetterPage />
        </ProtectedRoute>
      } />
      <Route path="/sustainability/me" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head', 'counselor', 'health_coach']}>
          <MySustainabilityPage />
        </ProtectedRoute>
      } />
      <Route path="/sustainability/team" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <CounselorSustainabilityPage />
        </ProtectedRoute>
      } />
      <Route path="/centre/war-room" element={
        <ProtectedRoute allowedRoles={['admin', 'terra_head']}>
          <WeeklyWarRoomPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/formulation/production/:subscriberId/:phaseId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminFormulationProduction />
        </ProtectedRoute>
      } />
      <Route path="/admin/formulation/inventory/:subscriberId/:phaseId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminFormulationInventory />
        </ProtectedRoute>
      } />
      <Route path="/admin/formulation/verification" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor']}>
          <AdminFormulationVerification />
        </ProtectedRoute>
      } />
      <Route path="/admin/formulation/snapshot/:subscriberId/:phaseId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminFormulationSnapshot />
        </ProtectedRoute>
      } />
      <Route path="/admin/formulation/feedback/:subscriberId/:phaseId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminFormulationFeedback />
        </ProtectedRoute>
      } />
      <Route path="/admin/formulation/transition-plan/:planId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminFormulationTransitionPlan />
        </ProtectedRoute>
      } />
      <Route path="/subscriber/transition-plan" element={
        <ProtectedRoute allowedRoles={['subscriber']}>
          <SubscriberTransitionPlan />
        </ProtectedRoute>
      } />
      <Route path="/subscriber/phase-preparation" element={
        <ProtectedRoute allowedRoles={['subscriber']}>
          <SubscriberPhasePreparation />
        </ProtectedRoute>
      } />
      <Route path="/admin/clinical" element={
        <ProtectedRoute allowedRoles={['admin', 'doctor']}>
          <AdminClinical />
        </ProtectedRoute>
      } />
      <Route path="/admin/mindflo-review" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminMindFloReview />
        </ProtectedRoute>
      } />
      <Route path="/admin/content" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminContent />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-workbench" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAIWorkbench />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/admin/audit" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAuditLog />
        </ProtectedRoute>
      } />
      <Route path="/admin/biological-context" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor']}>
          <AdminBiologicalContext />
        </ProtectedRoute>
      } />
      <Route path="/admin/bio-context" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor']}>
          <BioContextV2 />
        </ProtectedRoute>
      } />
      <Route path="/admin/biocontext-memory" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminBiologicalContextMemory />
        </ProtectedRoute>
      } />
      <Route path="/admin/governance" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor']}>
          <AdminGovernanceDrift />
        </ProtectedRoute>
      } />
      <Route path="/admin/support-visibility" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminSupportVisibility />
        </ProtectedRoute>
      } />
      <Route path="/admin/protocol-evolutions" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminProtocolEvolutions />
        </ProtectedRoute>
      } />
      <Route path="/admin/counselor-followup" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminCounselorFollowUp />
        </ProtectedRoute>
      } />
      <Route path="/admin/counselor-followup/:subId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminCounselorFollowUp />
        </ProtectedRoute>
      } />
      <Route path="/admin/journey" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'doctor']}>
          <JourneyManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/deployment" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DeploymentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/lab-intelligence" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminLabIntelligence />
        </ProtectedRoute>
      } />
      <Route path="/admin/decode-wizard" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDecodeWizard />
        </ProtectedRoute>
      } />
      <Route path="/admin/protocol-wizard" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminProtocolWizard />
        </ProtectedRoute>
      } />
      <Route path="/admin/protocol-flow" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor']}>
          <ProtocolFlowPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/executable-protocol" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <ExecutableProtocolReview />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ReportManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/content-publishing" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor']}>
          <ContentPublishing />
        </ProtectedRoute>
      } />
      <Route path="/admin/engagement" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <EngagementDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/support-os" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <SupportDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/autonomy-monitoring" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AutonomyMonitoring />
        </ProtectedRoute>
      } />
      <Route path="/admin/demo-simulation" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DemoSimulation />
        </ProtectedRoute>
      } />
      <Route path="/admin/demo-control" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DemoControlCenter />
        </ProtectedRoute>
      } />
      <Route path="/admin/care-command-center" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCommandCenter />
        </ProtectedRoute>
      } />
      <Route path="/admin/care-flow" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <CareFlowRoleSwitch />
        </ProtectedRoute>
      } />
      <Route path="/admin/execution-flow" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <CoachExecutionFlow />
        </ProtectedRoute>
      } />
      <Route path="/admin/org-structure" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <OrgStructure />
        </ProtectedRoute>
      } />
      <Route path="/centre/dashboard" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor', 'flow_steward', 'terra_head']}>
          <OpsCockpit />
        </ProtectedRoute>
      } />
      <Route path="/centre/subscribers" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor', 'flow_steward', 'terra_head']}>
          <OpsCockpit />
        </ProtectedRoute>
      } />
      <Route path="/centre/actions" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor', 'flow_steward', 'terra_head']}>
          <OpsCockpit />
        </ProtectedRoute>
      } />
      <Route path="/admin/twinflo-evolution/:subscriberId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor', 'flow_steward', 'terra_head']}>
          <TwinFloEvolutionPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/schedule-editor" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ScheduleEditor />
        </ProtectedRoute>
      } />
      <Route path="/admin/lifeflo-definitions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <LifeFloDefinitions />
        </ProtectedRoute>
      } />
      <Route path="/admin/medication-definitions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <MedicationDefinitions />
        </ProtectedRoute>
      } />
      <Route path="/admin/timeline-review" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <TimelineReview />
        </ProtectedRoute>
      } />
      <Route path="/admin/execution-intelligence" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <ExecutionIntelligence />
        </ProtectedRoute>
      } />
      <Route path="/admin/correlation-insights" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <CorrelationInsights />
        </ProtectedRoute>
      } />
      <Route path="/admin/pattern-narrative" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <PatternNarrative />
        </ProtectedRoute>
      } />
      <Route path="/admin/report-generator" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ReportGenerator />
        </ProtectedRoute>
      } />
      <Route path="/admin/data-management" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DataManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/assigned" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach']}>
          <AssignedSubscribers />
        </ProtectedRoute>
      } />
      <Route path="/admin/care-dashboard/:subscriberId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <CareTeamDashboardView />
        </ProtectedRoute>
      } />
      <Route path="/admin/impersonate/:subscriberId" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ImpersonateView />
        </ProtectedRoute>
      } />
      <Route path="/admin/wearables/:subscriberId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <AdminWearables />
        </ProtectedRoute>
      } />
      <Route path="/admin/insights/:subscriberId" element={
        <ProtectedRoute allowedRoles={['admin', 'counselor', 'health_coach', 'doctor']}>
          <CareTeamInsights />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
export { API, BACKEND_URL };
